import { Injector } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { map, take } from 'rxjs';
import {
  UpdatePaymentRequest,
  UpdatePaymentRequestFailed,
  UpdatePaymentRequestSuccess,
} from '../../../actions/payment-request.action';
import { PaymentDeclined } from '../../../actions/payment.action';
import { PaymentRequestUpdateRequest } from '../../../services/account-service/account.type';
import { NotificationState } from '../../../state/notifications.state';
import { getQueryParamValueByKey } from '../../query-params.utils';
import { handleRedirection } from '../cta-redirection.utils';

/**
 * Approves a payment request by dispatching an update action and handling the response.
 *
 * @param injector - The Angular injector used to retrieve services.
 * @param widgetProperties - Properties of the widget used for navigation and configuration.
 *
 * This function retrieves necessary services and state using the injector, checks if
 * payment request summary data is available, and dispatches an update action to approve
 * the payment request. It listens for specific actions to determine the outcome of the
 * request and navigates to appropriate pages based on success, failure, or decline.
 * If the payment request data is unavailable, it shows an error alert.
 */
export function approvePaymentRequest(injector: Injector, widgetProperties: BaseWidgetProperties) {
  const store = injector.get(Store);
  const actions = injector.get(Actions);
  const alertHandlerService = injector.get(AlertHandlerService);
  const paymentRequestSummary = store.selectSnapshot(NotificationState.getNotification).paymentRequestSummary;
  if (paymentRequestSummary) {
    actions
      .pipe(
        ofActionDispatched(UpdatePaymentRequestSuccess, PaymentDeclined, UpdatePaymentRequestFailed),
        take(1),
        map((actionDispatchedInstance) => {
          widgetProperties = assignUrlBasedOnActionDispatched(widgetProperties, actionDispatchedInstance);
          const type =
            actionDispatchedInstance instanceof UpdatePaymentRequestSuccess
              ? ActionButtonOutcomesType.SUCCESS
              : ActionButtonOutcomesType.FAILURE;
          handleRedirection(widgetProperties, injector, type, true);
        }),
      )
      .subscribe();

    const updatePayload: PaymentRequestUpdateRequest = {
      id: paymentRequestSummary.recordId,
      approvedStatus: 'approved',
      accessToken: getQueryParamValueByKey('confirmation'),
    };

    store.dispatch(new UpdatePaymentRequest(updatePayload));
    widgetProperties['urlToNavigate'] = 'payment-request-processing';
    handleRedirection(widgetProperties, injector, ActionButtonOutcomesType.PROCESSING, true);
  } else {
    alertHandlerService.showAlertFn('error', 'Payment request data not available, please try again');
    console.error('Payment request data not available, please try again');
  }
}

function assignUrlBasedOnActionDispatched(
  widgetProperties: BaseWidgetProperties,
  actionDispatchedInstance: UpdatePaymentRequestSuccess | PaymentDeclined | UpdatePaymentRequestFailed,
): BaseWidgetProperties {
  if (actionDispatchedInstance instanceof UpdatePaymentRequestSuccess) {
    widgetProperties['urlToNavigate'] = 'payment-request-success';
  }
  if (actionDispatchedInstance instanceof PaymentDeclined) {
    widgetProperties['urlToNavigate'] = 'payment-request-failed';
  }
  if (actionDispatchedInstance instanceof UpdatePaymentRequestFailed) {
    widgetProperties['zoneToNavigate'] = APP_ZONES.PROFILE;
    widgetProperties['urlToNavigate'] = 'notification-centre';
  }
  return widgetProperties;
}
